'use client'

import * as React from 'react'
import { useSignIn } from '@clerk/nextjs'
import Image from 'next/image'
import Link from 'next/link'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export default function SignInPage() {
  const { isLoaded, signIn, setActive } = useSignIn()
  const [isLoading, setIsLoading] = React.useState(false)
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [error, setError] = React.useState('')

  const handleOAuth = async () => {
    setIsLoading(true)
    if (!isLoaded || !signIn) {
      setIsLoading(false)
      return
    }
    try {
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: '/sign-in/sso-callback',
        redirectUrlComplete: '/dashboard',
      })
    } catch {
      setIsLoading(false)
    }
  }

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)
    if (!isLoaded || !signIn) {
      setIsLoading(false)
      return
    }
    try {
      const result = await signIn.create({
        identifier: email,
        password,
      })
      if (result.status === 'complete' && setActive) {
        await setActive({ session: result.createdSessionId })
        window.location.href = '/dashboard'
      } else {
        setError('Sign in could not be completed. Please try again.')
      }
    } catch (err: unknown) {
      const clerkErr = err as { errors?: { message: string }[] }
      setError(clerkErr.errors?.[0]?.message || 'Invalid email or password')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-between bg-background px-4 py-8">
      <div />
      <div className="w-full max-w-[400px]">
        <div className="flex flex-col items-center gap-6">
          <Image src="/favicons/haven-rounded.png" alt="Haven" width={48} height={48} className="rounded-xl" />

          <div className="text-center">
            <h1 className="font-serif text-2xl font-normal text-foreground">Welcome back</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">Sign in to your account</p>
          </div>

          <Button
            variant="outline"
            onClick={handleOAuth}
            disabled={isLoading || !isLoaded}
            className="h-10 w-full gap-3 rounded-full bg-card shadow-sm"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
              <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
            </svg>
            {isLoading ? 'Redirecting...' : 'Continue with Google'}
          </Button>

          <div className="flex w-full items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleEmailSignIn} className="flex w-full flex-col gap-4">
            <div>
              <Label htmlFor="email" className="mb-1.5">
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hello@example.com"
                className="h-10 rounded-full bg-card px-4 text-sm"
              />
            </div>
            <div>
              <Label htmlFor="password" className="mb-1.5">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-10 rounded-full bg-card px-4 text-sm"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex justify-end">
              <Link
                href="/forgot-password"
                className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                Forgot password?
              </Link>
            </div>
            <Button
              type="submit"
              disabled={isLoading || !isLoaded || !email || !password}
              className="h-10 w-full rounded-full"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground">
            Don&apos;t have an account?{' '}
            <Link href="/sign-up" className="font-medium text-foreground underline underline-offset-2 hover:text-primary">
              Sign up
            </Link>
          </p>
        </div>
      </div>

      <p className="mt-8 max-w-[400px] text-center text-xs leading-relaxed text-muted-foreground">
        By continuing, you agree to Haven&apos;s{' '}
        <Link href="/terms" className="underline underline-offset-2 hover:text-foreground">
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link href="/privacy" className="underline underline-offset-2 hover:text-foreground">
          Privacy Policy
        </Link>
        , and to receive periodic emails with updates.
      </p>
    </div>
  )
}
